import React, { useState } from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  Button,
  Tooltip,
  Pagination
} from 'carbon-components-react';
import { Add16, Help16 } from '@carbon/icons-react';
import { getNewDate, toLocalDateFormat } from 'utilities/helper.util';
import { Header } from 'redux/member/types';
import DisplayAnnouncementsRow from './DisplayAnnouncementsRow';

const PageSizes = [10, 20, 50];

const DisplayAnnouncements: React.FC<any> = ({
  announcements,
  handlePreviewEdit,
  handleMakeActive,
  handleDelete,
  handlePageChange,
  currentPage,
  pageSize,
  totalItems,
  handleAddAnnouncement
}) => {
  const [openTooltip, setOpenTooltip] = useState(false);

  const headers: Array<Header> = [
    {
      key: 'icon',
      header: '',
      isSortable: false
    },
    {
      key: 'title',
      header: 'Title',
      isSortable: false
    },
    {
      key: 'type',
      header: 'Type',
      isSortable: false
    },
    {
      key: 'roles',
      header: 'Roles',
      isSortable: false
    },
    {
      key: 'startDate',
      header: 'Start date',
      isSortable: false
    },
    {
      key: 'endDate',
      header: 'End date',
      isSortable: false
    },
    {
      key: 'status',
      header: 'Status',
      isSortable: false
    },
    {
      key: 'organisations',
      header: 'Organisations',
      isSortable: false
    }
  ];

  const getTransformedType = (category: string) => {
    switch (category) {
      case 'GENERAL':
        return 'General';
      case 'MEMBER':
        return 'Member';
      default:
        return category;
    }
  };

  const getTransformedRoles = (roles: any) => {
    const mappedRoles = roles.map((role: string) => {
      const simplifiedRole = [];
      switch (role) {
        case 'UNAUTHENTICATED':
          simplifiedRole.push('U');
          break;
        case 'AOADMIN':
        case 'AOVIEWER':
        case 'AOUSER':
          simplifiedRole.push('M');
          break;
        case 'ENQUIRER':
          simplifiedRole.push('E');
          break;
        case 'SYSADMIN':
          simplifiedRole.push('S');
          break;
        case 'DBYDADMIN':
          simplifiedRole.push('D');
          break;
      }
      return simplifiedRole;
    });

    if (mappedRoles.length >= 4) {
      return 'All';
    } else {
      return mappedRoles.join(' ');
    }
  };

  const getAnnouncementStatus = (startDate: any, endDate: any) => {
    const today = getNewDate().utc();
    const startDateObj = getNewDate(startDate).utc();
    const endDateObj = getNewDate(endDate).utc();
    if (endDateObj >= today && startDateObj <= today) {
      return 'Active';
    } else if (endDateObj <= today) {
      return 'Ended';
    } else {
      return 'Scheduled';
    }
  };

  const handleTooltipOver = () => {
    setOpenTooltip(true);
  };

  const handleTooltipLeave = () => {
    setOpenTooltip(false);
  };

  const HelpIcon = React.forwardRef(() => <Help16 className="help-icon" />);

  const filteredAnnouncements = announcements?.map((announcement: any) => ({
    id: announcement.id?.toString(),
    icon: announcement.icon,
    title: announcement.title,
    type: getTransformedType(announcement.noticeType),
    roles: getTransformedRoles(announcement.targetRoles),
    startDate: announcement.startAt
      ? toLocalDateFormat(announcement.startAt)
      : '',
    endDate: announcement.endAt ? toLocalDateFormat(announcement.endAt) : '',
    status: getAnnouncementStatus(announcement.startAt, announcement.endAt),
    organisations: announcement.Organisations
  }));

  return (
    <>
      <DataTable
        rows={filteredAnnouncements}
        headers={headers}
        isSortable={false}
      >
        {({ rows, getTableProps, getHeaderProps, getRowProps }: any) => {
          return (
            <TableContainer>
              <TableToolbar>
                <TableToolbarContent>
                  <Button
                    onClick={handleAddAnnouncement}
                    renderIcon={Add16}
                    iconDescription="Add announcement"
                  >
                    Add announcement
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    <TableHeader />
                    <TableHeader
                      key="title"
                      {...getHeaderProps({ key: 'title', header: 'Title' })}
                    >
                      <div>
                        <div>Title</div>
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="type"
                      {...getHeaderProps({
                        key: 'category',
                        header: 'Category'
                      })}
                    >
                      <div>
                        <div>Type</div>
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="roles"
                      {...getHeaderProps({ key: 'roles', header: 'Roles' })}
                    >
                      <div className="roles-header">
                        <div
                          onMouseOver={handleTooltipOver}
                          onMouseLeave={handleTooltipLeave}
                        >
                          <Tooltip
                            triggerText={''}
                            align="start"
                            open={openTooltip}
                            onChange={handleTooltipOver}
                            renderIcon={HelpIcon}
                          >
                            <div id="tooltip-body">
                              <p>E = Enquirer</p>
                              <p>D = BYDA</p>
                              <p>M = Member</p>
                              <p>S = Support</p>
                              <p>U = Unauthenticated</p>
                            </div>
                          </Tooltip>
                        </div>
                        <div>Roles</div>
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="startDate"
                      {...getHeaderProps({
                        key: 'startDate',
                        header: 'Start date'
                      })}
                    >
                      <div>
                        <div>Start date</div>
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="endDate"
                      {...getHeaderProps({
                        key: 'endDate',
                        header: 'End date'
                      })}
                    >
                      <div>
                        <div>End date</div>
                      </div>
                    </TableHeader>
                    <TableHeader
                      key="status"
                      {...getHeaderProps({ key: 'status', header: 'Status' })}
                    >
                      <div>
                        <div>Status</div>
                      </div>
                    </TableHeader>
                    <TableHeader></TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: any, index: any) => (
                    <DisplayAnnouncementsRow
                      row={row}
                      getRowProps={getRowProps}
                      key={index}
                      handlePreviewEdit={handlePreviewEdit}
                      handleMakeActive={handleMakeActive}
                      handleDelete={handleDelete}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>
      {filteredAnnouncements.length >= 1 && (
        <Pagination
          onChange={(data) => handlePageChange(data)}
          backwardText="Previous page"
          forwardText="Next page"
          itemsPerPageText="Items per page:"
          page={currentPage}
          pageNumberText="Page Number"
          pageSize={pageSize}
          pageSizes={PageSizes}
          totalItems={totalItems}
        />
      )}
    </>
  );
};

DisplayAnnouncements.displayName = 'DisplayAnnouncements';
export default DisplayAnnouncements;
